import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Loader2,
  Pencil,
  Trash2,
  Truck,
  TriangleAlert,
} from "lucide-react";
import clsx from "clsx";
import PrettyDate from "components/format/PrettyDate";
import useIsPhone from "features/files/use-is-phone";
import { Delivery, DeliveryItem, DeliveryStatus } from "types/Delivery";
import { useDeliveryActions } from "../hooks/useDeliveryActions";
import {
  buildTerminalasFields,
  DeliveryField,
  getDeliveryWarnings,
  warningText,
} from "./TerminalasDeliveryDetails";

interface DeliveriesTableProps {
  deliveries: Delivery[];
  onEdit: (delivery: Delivery) => void;
  onRefresh: () => void;
}

const COLUMN_COUNT = 8;
const EMPTY = "—";

// One tone per status, in the new (Tailwind) stack — same meaning as the
// Polaris badge tones used by the delivery card and the fulfillment summary.
const STATUS_PILL_CLASSES: Record<DeliveryStatus, string> = {
  draft: "bg-zinc-500/15 text-zinc-700 dark:bg-zinc-500/10 dark:text-zinc-300",
  planned: "bg-amber-500/15 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400",
  shipped: "bg-orange-500/15 text-orange-700 dark:bg-orange-500/10 dark:text-orange-400",
  delivered: "bg-green-500/15 text-green-700 dark:bg-green-500/10 dark:text-green-400",
  cancelled: "bg-red-500/15 text-red-700 dark:bg-red-500/10 dark:text-red-400",
};

const quantityOf = (item: DeliveryItem) => Number(item.quantity) || 0;

const deliveryTonnes = (delivery: Delivery) =>
  (delivery.items || []).reduce((sum, item) => sum + quantityOf(item), 0);

function describeProduct(item: DeliveryItem): { sku: string; title: string } {
  if (item.product && typeof item.product === "object") {
    return { sku: item.product.sku || "", title: item.product.title || item.product.id || "" };
  }
  return { sku: "", title: item.product || "" };
}

const productLabel = (item: DeliveryItem) => {
  const { sku, title } = describeProduct(item);
  return [sku, title].filter(Boolean).join(" ") || EMPTY;
};

/** Tonnes with up to 3 decimals, no trailing zeros, in the UI language. */
function useTonnesFormatter() {
  const { i18n } = useTranslation();
  // Typed as the local i18next declaration, which omits the runtime API.
  const language = (i18n as any).language as string | undefined;
  return useMemo(() => {
    const formatter = new Intl.NumberFormat(language, { maximumFractionDigits: 3 });
    return (tonnes: number) => formatter.format(tonnes);
  }, [language]);
}

function StatusPill({ status }: { status: DeliveryStatus }) {
  const { t } = useTranslation();
  return (
    <span
      className={clsx(
        "inline-flex items-center whitespace-nowrap rounded-md px-1.5 py-0.5 text-xs font-medium",
        STATUS_PILL_CLASSES[status] || STATUS_PILL_CLASSES.draft
      )}
    >
      {t(`deliveries_status_${status}`)}
    </span>
  );
}

function ProductsCell({ delivery }: { delivery: Delivery }) {
  const { t } = useTranslation();
  const items = delivery.items || [];
  if (items.length === 0) return <>{EMPTY}</>;

  const first = productLabel(items[0]);
  return (
    <span className="flex max-w-[20rem] items-center gap-1.5">
      <span className="truncate" title={items.map(productLabel).join("\n")}>
        {first}
      </span>
      {items.length > 1 && (
        <span className="shrink-0 text-xs text-zinc-500 dark:text-zinc-400">
          {t("deliveries_items_more", { count: items.length - 1 })}
        </span>
      )}
    </span>
  );
}

function SourceCell({ delivery, warnings }: { delivery: Delivery; warnings: string[] }) {
  const { t } = useTranslation();
  const isTerminalas = delivery.source === "terminalas";
  const code = delivery.externalOrderCode;

  return (
    <div className="flex items-center gap-2">
      {isTerminalas ? (
        <span className="inline-flex items-center gap-1.5">
          <Truck className="size-3.5 shrink-0 text-sky-600 dark:text-sky-400" aria-hidden="true" />
          <span>{t("deliveries_terminalas_badge")}</span>
          {delivery.externalUrl ? (
            <a
              href={delivery.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => event.stopPropagation()}
              className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
            >
              {code || t("deliveries_terminalas_order_code")}
              <ExternalLink className="size-3" aria-hidden="true" />
            </a>
          ) : code ? (
            <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">{code}</span>
          ) : null}
        </span>
      ) : (
        <span>{t("deliveries_source_manual")}</span>
      )}
      {warnings.length > 0 && (
        <span
          role="img"
          aria-label={warnings.join("; ")}
          title={warnings.join("\n")}
          className="inline-flex shrink-0 text-amber-600 dark:text-amber-400"
        >
          <TriangleAlert className="size-4" aria-hidden="true" />
        </span>
      )}
    </div>
  );
}

interface DetailField extends DeliveryField {
  wide?: boolean;
}

interface DeliveryDetailsProps {
  delivery: Delivery;
  warnings: string[];
  formatTonnes: (tonnes: number) => string;
  canEdit: boolean;
  canDelete: boolean;
  isDeleting: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

function DeliveryDetails({
  delivery,
  warnings,
  formatTonnes,
  canEdit,
  canDelete,
  isDeleting,
  onEdit,
  onDelete,
}: DeliveryDetailsProps) {
  const { t } = useTranslation();
  const isTerminalas = delivery.source === "terminalas";
  const items = delivery.items || [];
  // A single plain item is already spelled out in the collapsed row (code,
  // name, tonnes); the items table only earns its place when it adds something.
  const showItemsTable =
    items.length > 1 || items.some((item) => !!item.reservationNumber || !!item.notes);

  // The plate is already in the collapsed row, so the grid starts at the driver.
  const truckFields: DetailField[] = isTerminalas
    ? buildTerminalasFields(t, delivery, formatTonnes).filter((field) => field.key !== "plate")
    : [{ key: "carrier", label: t("carrier"), value: delivery.carrier }];
  const fields: DetailField[] = [
    ...truckFields,
    { key: "createdBy", label: t("created_by"), value: delivery.createdBy?.name },
    { key: "notes", label: t("notes"), value: delivery.notes, wide: true },
  ].filter((field) => field.value !== null && field.value !== undefined && field.value !== "");

  return (
    <div className="space-y-3 text-sm">
      {fields.length > 0 && (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3 lg:grid-cols-4">
          {fields.map((field) => (
            <div key={field.key} className={clsx(field.wide && "col-span-full")}>
              <dt className="text-xs text-zinc-500 dark:text-zinc-400">{field.label}</dt>
              <dd className="whitespace-pre-line font-medium text-zinc-900 dark:text-zinc-100">
                {field.value}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {warnings.length > 0 && (
        <ul className="space-y-1">
          {warnings.map((warning, index) => (
            <li
              key={`${warning}-${index}`}
              className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400"
            >
              <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
              {warning}
            </li>
          ))}
        </ul>
      )}

      {showItemsTable ? (
        <div className="overflow-x-auto rounded-md border border-zinc-200 dark:border-zinc-700">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-zinc-500 dark:text-zinc-400">
                <th scope="col" className="px-2 py-1.5 font-medium">{t("sku")}</th>
                <th scope="col" className="px-2 py-1.5 font-medium">{t("product_name")}</th>
                <th scope="col" className="px-2 py-1.5 font-medium">{t("reservation")}</th>
                <th scope="col" className="px-2 py-1.5 text-right font-medium">{t("quantity")}</th>
                <th scope="col" className="px-2 py-1.5 font-medium">{t("notes")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {items.map((item) => {
                const { sku, title } = describeProduct(item);
                return (
                  <tr key={item.id} className="text-zinc-900 dark:text-zinc-100">
                    <td className="whitespace-nowrap px-2 py-1.5">{sku || EMPTY}</td>
                    <td className="px-2 py-1.5">{title || EMPTY}</td>
                    <td className="px-2 py-1.5">{item.reservationNumber || EMPTY}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">
                      {formatTonnes(quantityOf(item))}
                    </td>
                    <td className="px-2 py-1.5">{item.notes || EMPTY}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : items.length === 0 ? (
        <p className="text-xs text-zinc-500 dark:text-zinc-400">{t("deliveries_no_items")}</p>
      ) : null}

      {isTerminalas && (
        <p className="text-xs text-zinc-500 dark:text-zinc-400">
          {t("deliveries_terminalas_managed_notice")}
        </p>
      )}

      {(canEdit || canDelete) && (
        <div className="flex flex-wrap justify-end gap-2">
          {canEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-200/60 max-md:min-h-11 max-md:px-3 max-md:text-sm dark:text-zinc-200 dark:hover:bg-zinc-700/60"
            >
              <Pencil className="size-3.5" aria-hidden="true" />
              {t("edit")}
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={onDelete}
              disabled={isDeleting}
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-60 max-md:min-h-11 max-md:px-3 max-md:text-sm dark:text-red-400"
            >
              {isDeleting ? (
                <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
              ) : (
                <Trash2 className="size-3.5" aria-hidden="true" />
              )}
              {t("delete")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

interface DeliveryRowProps {
  delivery: Delivery;
  expanded: boolean;
  onToggle: (id: string) => void;
  onEdit: (delivery: Delivery) => void;
  onRefresh: () => void;
  formatTonnes: (tonnes: number) => string;
}

/** What the card and the table row both show of a delivery, and what its buttons do. */
function useDeliveryRowFacts(delivery: Delivery, expanded: boolean, onRefresh: () => void) {
  const { t } = useTranslation();
  const actions = useDeliveryActions(delivery, onRefresh);
  return {
    ...actions,
    detailsId: `delivery-${delivery.id}-details`,
    warnings: getDeliveryWarnings(delivery).map((warning) => warningText(t, warning)),
    date: actions.isTerminalas ? delivery.loadedAt || delivery.deliveryDate : delivery.deliveryDate,
    toggleLabel: `${t(expanded ? "deliveries_hide_details" : "deliveries_show_details")}: ${t(
      "deliveries_number",
      { number: delivery.number }
    )}`,
  };
}

/** A delivery as a card, for a phone. */
function PhoneDeliveryCard({
  delivery,
  expanded,
  onToggle,
  onEdit,
  onRefresh,
  formatTonnes,
}: DeliveryRowProps) {
  const { t } = useTranslation();
  const { canEdit, canDelete, isDeleting, handleDelete, detailsId, warnings, date, toggleLabel } =
    useDeliveryRowFacts(delivery, expanded, onRefresh);
  const Chevron = expanded ? ChevronDown : ChevronRight;

  return (
    <li className="rounded-lg border border-zinc-200 dark:border-zinc-700">
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={expanded ? detailsId : undefined}
        aria-label={toggleLabel}
        onClick={() => onToggle(delivery.id)}
        className="flex min-h-11 w-full items-start gap-2 p-3 text-left"
      >
        <Chevron className="mt-0.5 size-4 shrink-0 text-zinc-500" aria-hidden="true" />
        <span className="min-w-0 flex-1 space-y-1">
          <span className="flex items-center justify-between gap-2 text-sm font-medium">
            <span>
              #{delivery.number} · {date ? <PrettyDate dateString={date} /> : EMPTY}
            </span>
            <StatusPill status={delivery.status} />
          </span>
          <span className="flex items-center justify-between gap-2 text-sm">
            <span className="font-semibold">
              {delivery.vehiclePlate || delivery.carrier || EMPTY}
            </span>
            <span className="tabular-nums">
              {t("deliveries_col_quantity_t")}: {formatTonnes(deliveryTonnes(delivery))}
            </span>
          </span>
          <span className="block text-sm">
            <ProductsCell delivery={delivery} />
          </span>
        </span>
      </button>
      <div className="px-3 pb-3 text-sm">
        <SourceCell delivery={delivery} warnings={warnings} />
      </div>
      {expanded && (
        <div id={detailsId} className="border-t border-zinc-200 p-3 dark:border-zinc-700">
          <DeliveryDetails
            delivery={delivery}
            warnings={warnings}
            formatTonnes={formatTonnes}
            canEdit={canEdit}
            canDelete={canDelete}
            isDeleting={isDeleting}
            onEdit={() => onEdit(delivery)}
            onDelete={handleDelete}
          />
        </div>
      )}
    </li>
  );
}

/** A delivery as a table row, with its details in a row below when expanded. */
function DeliveryRow({
  delivery,
  expanded,
  onToggle,
  onEdit,
  onRefresh,
  formatTonnes,
}: DeliveryRowProps) {
  const { canEdit, canDelete, isDeleting, handleDelete, detailsId, warnings, date, toggleLabel } =
    useDeliveryRowFacts(delivery, expanded, onRefresh);
  const Chevron = expanded ? ChevronDown : ChevronRight;

  return (
    <>
      <tr
        onClick={() => {
          // Selecting a plate or order code to copy it must not fold the row.
          if (window.getSelection()?.toString()) return;
          onToggle(delivery.id);
        }}
        className={clsx(
          "cursor-pointer text-zinc-900 hover:bg-zinc-50 dark:text-zinc-100 dark:hover:bg-zinc-800/40",
          expanded
            ? "bg-zinc-50/60 dark:bg-zinc-800/40"
            : "border-b border-zinc-200 last:border-b-0 dark:border-zinc-700"
        )}
      >
        <td className="w-8 py-2 pl-2 pr-0">
          {/* The row's click handler does the toggling; the button makes it
              reachable and announced for keyboard and screen-reader users. */}
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls={expanded ? detailsId : undefined}
            aria-label={toggleLabel}
            className="inline-flex rounded p-0.5 text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100"
          >
            <Chevron className="size-4" aria-hidden="true" />
          </button>
        </td>
        <td className="whitespace-nowrap px-3 py-2 font-medium">#{delivery.number}</td>
        <td className="whitespace-nowrap px-3 py-2">
          {date ? <PrettyDate dateString={date} /> : EMPTY}
        </td>
        <td className="whitespace-nowrap px-3 py-2 font-semibold">
          {delivery.vehiclePlate || delivery.carrier || EMPTY}
        </td>
        <td className="px-3 py-2">
          <ProductsCell delivery={delivery} />
        </td>
        <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
          {formatTonnes(deliveryTonnes(delivery))}
        </td>
        <td className="px-3 py-2">
          <StatusPill status={delivery.status} />
        </td>
        <td className="px-3 py-2">
          <SourceCell delivery={delivery} warnings={warnings} />
        </td>
      </tr>
      {expanded && (
        <tr id={detailsId} className="border-b border-zinc-200 last:border-b-0 dark:border-zinc-700">
          <td
            colSpan={COLUMN_COUNT}
            className="bg-zinc-50/60 pb-3 pl-10 pr-3 pt-1 dark:bg-zinc-800/40"
          >
            <DeliveryDetails
              delivery={delivery}
              warnings={warnings}
              formatTonnes={formatTonnes}
              canEdit={canEdit}
              canDelete={canDelete}
              isDeleting={isDeleting}
              onEdit={() => onEdit(delivery)}
              onDelete={handleDelete}
            />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * An order's deliveries as a compact table: one collapsed row per delivery
 * (truck), details one click away. Expanded rows are remembered by delivery id
 * so a refresh or re-sort of the list does not collapse what the user opened.
 */
export default function DeliveriesTable({ deliveries, onEdit, onRefresh }: DeliveriesTableProps) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const formatTonnes = useTonnesFormatter();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());

  const rows = useMemo(() => deliveries.filter((delivery) => delivery && delivery.id), [deliveries]);

  const handleToggle = useCallback((id: string) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }, []);

  const totals = useMemo(() => {
    const active = rows.filter((delivery) => delivery.status !== "cancelled");
    return {
      count: active.length,
      tonnes: active.reduce((sum, delivery) => sum + deliveryTonnes(delivery), 0),
    };
  }, [rows]);

  if (rows.length === 0) return null;

  const totalLine = (
    <p className="mt-2 text-right text-sm font-medium text-zinc-700 tabular-nums dark:text-zinc-200">
      {t("deliveries_total", { count: totals.count, tonnes: formatTonnes(totals.tonnes) })}
    </p>
  );

  // The table is 48rem wide, which a phone shows only by scrolling sideways;
  // there each delivery is a card of its own.
  if (isPhone) {
    return (
      <div>
        <ul className="space-y-2">
          {rows.map((delivery) => (
            <PhoneDeliveryCard
              key={delivery.id}
              delivery={delivery}
              expanded={expandedIds.has(delivery.id)}
              onToggle={handleToggle}
              onEdit={onEdit}
              onRefresh={onRefresh}
              formatTonnes={formatTonnes}
            />
          ))}
        </ul>
        {totalLine}
      </div>
    );
  }

  const headerClass = "px-3 py-2 text-left text-xs font-medium text-zinc-500 dark:text-zinc-400";

  return (
    <div>
      <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-700">
        <table className="w-full min-w-[48rem] text-sm">
          <thead className="border-b border-zinc-200 dark:border-zinc-700">
            <tr>
              <th scope="col" className="w-8" />
              <th scope="col" className={headerClass}>{t("deliveries_col_number")}</th>
              <th scope="col" className={headerClass}>{t("deliveries_col_date")}</th>
              <th scope="col" className={headerClass}>{t("deliveries_col_vehicle")}</th>
              <th scope="col" className={headerClass}>{t("deliveries_col_products")}</th>
              <th scope="col" className={clsx(headerClass, "text-right")}>
                {t("deliveries_col_quantity_t")}
              </th>
              <th scope="col" className={headerClass}>{t("deliveries_col_status")}</th>
              <th scope="col" className={headerClass}>{t("deliveries_col_source")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((delivery) => (
              <DeliveryRow
                key={delivery.id}
                delivery={delivery}
                expanded={expandedIds.has(delivery.id)}
                onToggle={handleToggle}
                onEdit={onEdit}
                onRefresh={onRefresh}
                formatTonnes={formatTonnes}
              />
            ))}
          </tbody>
        </table>
      </div>
      {totalLine}
    </div>
  );
}
